// @flow

import * as React from 'react';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import green from '@mui/material/colors/green';
import blue from '@mui/material/colors/blue';
import red from '@mui/material/colors/red';
import Icon from '@mui/material/Icon';
import * as jsondiffpatch from 'jsondiffpatch';

import { date, time } from 'app/lib/format';
import { TimelineEventData } from './timeline';

type Props = {
  users: Array<mixed>,
  data: TimelineEventData
};

type State = {
  expanded: boolean
};

class ActivityTimelineEvent extends React.PureComponent<Props, State> {
  state = {
    expanded: false
  };

  toggle = () => this.setState({ expanded: !this.state.expanded });

  renderIcon() {
    const action = this.props.data.action;
    let icon;
    let iconColor = blue[600];

    switch (action) {
      case 'dwelling/publish_error':
        icon = 'error';
        break;
      case 'dwelling/create':
        icon = 'add';
        break;
      case 'dwelling/update':
        icon = 'edit';
        break;
      case 'dwelling/approved':
        icon = 'spellcheck';
        iconColor = green[600];
        break;
      case 'dwelling/rejected':
        icon = 'block';
        iconColor = red[600];
        break;
      case 'dwelling/deactivate':
        icon = 'clear';
        iconColor = red[600];
        break;
      case 'dwelling/activate':
        icon = 'replay';
        iconColor = green[600];
        break;
      case 'dwelling/payment':
        icon = 'payment';
        iconColor = green[600];
        break;
      case 'dwelling/requested-ownership-mail':
      case 'dwelling/email':
        icon = 'mailOutline';
        break;
      case 'dwelling/system': {
        icon = 'settings';
        break;
      }
      case 'dwelling/move-to-admin': {
        icon = 'delete';
        break;
      }
      case 'notes':
        icon = 'edit';
        iconColor = '#999999';
        break;
      default:
        icon = 'help';
        iconColor = '#999999';
        break;
    }

    return {
      icon,
      iconColor
    };
  }

  getData() {
    const { action, data } = this.props.data;
    switch (action) {
      case 'dwelling/publish_error': {
        const errorMessage = data && data.error;
        return (
          <div>
            <span style={{ color: 'red' }}>{`Error : ${errorMessage}`}</span>
          </div>
        );
      }
      case 'dwelling/update': {
        if (this.state.expanded && data && data.diff) {
          return (
            <div
              dangerouslySetInnerHTML={{
                __html: jsondiffpatch.formatters.html.format(data.diff)
              }}
            />
          );
        }
        return null;
      }
      case 'dwelling/approved': {
        const externalId = data && data.externalId;
        const externalLink =
          Number.isInteger(externalId) &&
          `https://www.akutbolig.dk/vis/${externalId}`;
        return (
          <div>
            {externalLink && (
              <>
                <a
                  href={externalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {externalLink}
                </a>
                <br />
              </>
            )}
            {data && data.reason}
          </div>
        );
      }
      case 'dwelling/payment': {
        return <pre>{JSON.stringify(data && data.diff, null, 2)}</pre>;
      }
      case 'dwelling/requested-ownership-mail': {
        return <div>{data && data.reason ? data.reason : null}</div>;
      }
      case 'dwelling/email': {
        return (
          <div>
            email type: {data && data.type}
            <br />
            {data && data.status ? `status: ${data.status}` : null}
            <br />
            {data && data.scheduledTime
              ? `scheduled time: ${data.scheduledTime}`
              : null}
            {data && data.reason ? data.reason : null}
          </div>
        );
      }
      default: {
        return (data && data.reason) || null;
      }
    }
  }

  renderTitle(event, users, admin, ip) {
    const user =
      !admin &&
      users.size &&
      users.find((user) => user.get('_id') === event.actor);

    if (admin && /[/]/.test(event.actor)) {
      return (
        <span>
          <span style={{ fontWeight: 'bold' }}>{event.actor.split('/')[1]}</span>{' '}
          ({event.actor.split('/')[0]})
        </span>
      );
    }

    if (event.actor === 'system') {
      return 'System';
    }

    if (user) {
      return (
        `${user.get('firstname') || ''} ${user.get('lastname') || ''} \n${
          user.get('email') || ''
        } ${ip ? `(IP: ${ip})` : ''}`.trim() ||
        user.get('email') ||
        user.get('phone')
      );
    }

    return `Udført af ${/admin/.test(event.actor) ? 'admin' : 'brugeren'} ${
      ip ? `(IP: ${ip})` : ''
    }`;
  }

  render() {
    const { data: event, users } = this.props;
    const timestamp = new Date(event.timestamp);
    const admin = /^admin/.test(event.actor);
    const ip = event && event.data && event.data.ip;
    const { icon, iconColor } = this.renderIcon();

    return (
      <div
        style={{
          position: 'relative',
          marginBottom: 16,
          whiteSpace: 'pre-line',
          wordBreak: 'break-word'
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: -30,
            top: 12,
            width: 30,
            height: 30,
            borderRadius: '50%',
            backgroundColor: '#ffffff',
            border: `2px solid ${iconColor}`,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: iconColor,
            zIndex: 1
          }}
          title={event.action}
          onClick={this.toggle}
        >
          {icon ? (
            <Icon style={{ fontSize: 18 }}>
              {icon}
            </Icon>
          ) : null}
        </div>

        <Paper
          elevation={1}
          style={{
            padding: 12
          }}
        >
          <Typography
            variant="subtitle2"
            style={{
              color: admin ? '#555555' : '#111111'
            }}
          >
            {this.renderTitle(event, users, admin, ip)}
          </Typography>

          <Typography
            variant="caption"
            color="textSecondary"
            style={{ display: 'block', marginTop: 4, marginBottom: 8 }}
          >
            {`${date.format(timestamp)}, kl. ${time.format(timestamp)}`}
          </Typography>

          {this.getData()}
        </Paper>
      </div>
    );
  }
}

export default ActivityTimelineEvent;